import Image from 'next/legacy/image';
import { FC } from 'react';
import { useRetriveCart } from 'reactQuery/queries';
import { OrderSummaryProps, Styled, shouldDisplaySavings } from './';
import { useI18n } from 'contexts/i18n';
import { CouponCodeDetails } from 'components/CouponCodeDetails';
import { isAPACStore } from 'utils';

const OrderSummary: FC<OrderSummaryProps> = ({ orderStep }) => {
  const { data: currentCart } = useRetriveCart();
  const i18n = useI18n();

  return (
    <Styled.OrderSummaryContainer>
      <Styled.Title>{i18n['Order Summary']} </Styled.Title>
      <Styled.LineItemsContainer>
        {currentCart?.lineItems.map(lineItem => {
          return (
            <Styled.LineItemContainer key={lineItem.id}>
              <Styled.LineItemImage>
                <Image
                  src={lineItem.imageUrl}
                  height={80}
                  width={80}
                  alt={lineItem.name}
                />
              </Styled.LineItemImage>
              <Styled.LineItemDescription>
                <Styled.ItemName>{lineItem.name}</Styled.ItemName>

                {lineItem.isSubscribed ? (
                    <Styled.SubscriptionDetails>
                      {`${i18n['Subscription:']} ${lineItem.quantity} ${
                        lineItem.quantity === 1 ? i18n['Unit'] : i18n['Units']
                      } ${i18n['delivered and billed every']} ${
                        lineItem.subscriptionTitle
                          ? i18n[lineItem.subscriptionTitle] ||
                            lineItem.subscriptionTitle
                          : ''
                      }`}
                      <Styled.TotalSaving>
                        (
                        {`${parseInt(lineItem.discountPercentage)}% ${
                          i18n['saved']
                        }`}
                        )
                      </Styled.TotalSaving>
                    </Styled.SubscriptionDetails>
                ) : (
                  <Styled.Quantity>
                    {`${i18n['Quantity']}: ${lineItem.quantity}`}
                  </Styled.Quantity>
                )}
              </Styled.LineItemDescription>
              <Styled.LineItemPrice>
                {lineItem.displayTotalWithoutDiscount}
              </Styled.LineItemPrice>
            </Styled.LineItemContainer>
          );
        })}
      </Styled.LineItemsContainer>
      <Styled.Separator />
      <Styled.SubTotal>
        <div>{i18n['SubTotal']}</div>
        <div>{currentCart?.displayLineItemsTotalWithoutDiscount}</div>
      </Styled.SubTotal>
      {isAPACStore() && (
        <Styled.SubTotal>
          <div>{i18n['Points Redeemed']}</div>
          <div>{currentCart?.totalRedeemPoints}</div>
        </Styled.SubTotal>
      )}
      <>
        <Styled.SubTotal>
          <div>{i18n['Shipping fee']}</div>
          <div>
            {currentCart?.displayShipTotal !== '' &&
            currentCart?.state !== 'address' ? (
              currentCart?.displayShipTotal
            ) : (
              <div>{i18n['Calculated at next step']}</div>
            )}
          </div>
        </Styled.SubTotal>
        <Styled.SubTotal>
          <div>{i18n['Tax']}</div>
          <div>
            {currentCart && currentCart?.state === 'address' ? (
              <div>{i18n['Calculated at next step']}</div>
            ) : (
              currentCart?.displayTaxTotal
            )}
          </div>
        </Styled.SubTotal>
        {shouldDisplaySavings(currentCart?.withholdingTaxTotal ?? '') && (
          <Styled.SubTotal>
            <div>{i18n['Withholding Tax']}</div>
            <div>
              {currentCart && currentCart?.state === 'address' ? (
                <div>{i18n['Calculated at next step']}</div>
              ) : (
                currentCart?.withholdingTaxTotal
              )}
            </div>
          </Styled.SubTotal>
        )}
      </>
      {currentCart && shouldDisplaySavings(currentCart.lineItemSavings) && (
        <Styled.Discount>
          <div>{i18n['Discount']}</div>
          <div>{currentCart?.lineItemSavings}</div>
        </Styled.Discount>
      )}

      {currentCart && shouldDisplaySavings(currentCart.shipmentSavings) && (
        <Styled.Discount>
          <div>{i18n['Free Shipping']}</div>
          <div>{currentCart?.shipmentSavings}</div>
        </Styled.Discount>
      )}
      <Styled.Separator />

      {currentCart && currentCart.promotions.length > 0 && (
        <>
          <CouponCodeDetails promotions={currentCart.promotions} />
          <Styled.Separator />
        </>
      )}

      <Styled.Total>
        <div>{i18n['Total']}</div>
        <div>{currentCart?.displayTotal}</div>
      </Styled.Total>
    </Styled.OrderSummaryContainer>
  );
};

export default OrderSummary;
